// PresetSelector.jsx
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Stack,
  Typography,
  Tooltip,
  ButtonBase,
  IconButton,
  Collapse,
  Button,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { keyframes } from '@mui/system';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alpha } from '@mui/material/styles';
import { setSelectedSinglePreset, deletePreset } from 'stores/sensor/controllersSlice';
import EditIcon from '@mui/icons-material/Edit';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import { LOCK_LABELS } from '../../../constants/lockOptions';

const HEX = {
  white: '#FFFFFF',
  gridBorder: '#bcbcbcff',
  hoverOutline: '#585858ff',
  hourBg: '#F5F5F5',
  dayBg: '#FAFAFA',
  text: '#111111ff',
  subText: '#484848ff',
};

const shake = keyframes`
  0% { transform: translateX(0) rotate(0deg); }
  20% { transform: translateX(-1px) rotate(-0.5deg); }
  40% { transform: translateX( 1px) rotate( 0.5deg); }
  60% { transform: translateX(-1px) rotate(-0.4deg); }
  80% { transform: translateX( 1px) rotate( 0.4deg); }
  100% { transform: translateX(0) rotate(0deg); }
`;

const PresetCard = ({ preset, selected, onSelect, onDelete, deleteMode }) => {
  const isRelayPreset = String(preset.device_type ?? '')
    .toLowerCase()
    .includes('relay');
  const smartModeValue = preset.smart_mode ?? preset.control === 'solai';
  const isSmartMode = smartModeValue === true || String(smartModeValue).toLowerCase() === 'true';
  const relayPower =
    preset.power ?? (preset.channel_1 === 1 || preset.channel_2 === 1 ? 'on' : 'off');
  const tooltip = deleteMode ? (
    <Box>
      <Typography variant="subtitle2">Delete preset?</Typography>
      <Typography variant="body2">Click to remove “{preset.name}”.</Typography>
    </Box>
  ) : (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
      {isRelayPreset ? (
        <>
          <Typography variant="body2">
            Smart mode: <strong>{isSmartMode ? 'ON' : 'OFF'}</strong>
          </Typography>
          <Typography variant="body2">
            Mode: <strong>{String(preset.mode ?? 'Cool').toUpperCase()}</strong>
          </Typography>
          <Typography variant="body2">
            Comfort Mode: <strong>{preset.comfort_mode ?? 'Neutral'}</strong>
          </Typography>
          {!isSmartMode && (
            <Typography variant="body2">
              Power: <strong>{String(relayPower).toUpperCase()}</strong>
            </Typography>
          )}
        </>
      ) : (
        <>
          <Typography variant="body2">
            Smart mode: <strong>{isSmartMode ? 'ON' : 'OFF'}</strong>
          </Typography>
          {!isSmartMode && (
            <Typography variant="body2">
              Power: <strong>{preset.power.toUpperCase()}</strong>
            </Typography>
          )}
          <Typography variant="body2">
            Mode: <strong>{preset.mode.toUpperCase()}</strong>
          </Typography>
          <Typography variant="body2">
            Temp: <strong>{preset.temp}°C</strong>
          </Typography>
          <Typography variant="body2">
            Fan: <strong>{preset.fan.toUpperCase()}</strong>
          </Typography>
          {preset.lock !== undefined && (
            <Typography variant="body2">
              Lock: <strong>{LOCK_LABELS[preset.lock] ?? LOCK_LABELS[0]}</strong>
            </Typography>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Tooltip
      placement="bottom"
      title={tooltip}
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: preset.color,
            color: '#fff',
            boxShadow: '1px 3px 10px 0px rgba(0,0,0,0.75)',
          },
        },
        arrow: { sx: { color: preset.color } },
      }}>
      <ButtonBase
        onClick={deleteMode ? onDelete : onSelect}
        sx={{
          position: 'relative',
          borderRadius: 2,
          px: 1.5,
          py: 1,
          bgcolor: '#fff',
          border: '3px solid',
          borderColor: deleteMode ? 'error.light' : selected ? preset.color : 'divider',
          transition: 'transform 120ms ease, box-shadow 120ms ease',
          minWidth: 140,
          animation: deleteMode ? `${shake} 0.35s ease-in-out infinite` : 'none',
          '&:hover': { transform: deleteMode ? 'none' : 'translateY(-1px)' },
        }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 15,
              height: 15,
              borderRadius: '3px',
              bgcolor: preset.color,
              boxShadow: 1,
            }}
          />
          <Typography variant="body2" sx={{ color: 'black', fontWeight: 600 }} noWrap>
            {preset.name}
          </Typography>
        </Stack>
      </ButtonBase>
    </Tooltip>
  );
};

const isRelayDeviceType = deviceType =>
  String(deviceType ?? '')
    .toLowerCase()
    .includes('relay');

export default function PresetSelector({
  removeMode = false,
  setRemoveMode,
  showRelayPresets,
  devices = [],
}) {
  const dispatch = useDispatch();
  const allPresets = useSelector(state => state.controllers.hourlyPresetsArray) || [];
  const scheduleDeviceTypes = useMemo(
    () => (devices || []).map(device => device?.device_type).filter(Boolean),
    [devices],
  );
  const onlyRelayPresets = useMemo(() => {
    if (scheduleDeviceTypes.length > 0) {
      return scheduleDeviceTypes.every(isRelayDeviceType);
    }

    return Boolean(showRelayPresets);
  }, [scheduleDeviceTypes, showRelayPresets]);
  const presets = useMemo(
    () =>
      allPresets.filter(preset => {
        const isPresetRelay = isRelayDeviceType(preset.device_type);
        const hasScheduleDeviceTypes = scheduleDeviceTypes.length > 0;

        if (onlyRelayPresets) {
          return isPresetRelay;
        }

        if (isPresetRelay) {
          return false;
        }

        if (!preset.device_type) {
          return true;
        }

        return hasScheduleDeviceTypes && scheduleDeviceTypes.includes(preset.device_type);
      }),
    [allPresets, onlyRelayPresets, scheduleDeviceTypes],
  );
  const selectedId = useSelector(state => state.controllers.selectedSinglePreset?.id) ?? null;

  const [deleteMode, setDeleteMode] = useState(false);
  const removeModeActive = Boolean(removeMode);

  const count = presets.length;

  const handleSelect = preset => {
    if (deleteMode) return;
    setRemoveMode(false);
    dispatch(setSelectedSinglePreset(preset));
  };

  const handleDelete = id => {
    dispatch(deletePreset(id));
  };

  return (
    <Box className="PresetSelectorPresetSelector" sx={{ py: '.5rem', mt: '.1rem' }}>
      {/* Collapsible content */}
      <>
        <Box
          flexWrap="wrap"
          gap={1.25}
          // alignItems="center"
          justifyContent="space-between">
          <Box display="flex" direction="row" flexWrap="wrap" gap={1}>
            <Tooltip
              title={deleteMode ? 'Exit delete mode' : 'Delete presets (click to enable)'}
              PopperProps={{
                sx: {
                  zIndex: 50001,
                },
              }}
              slotProps={{
                popper: {
                  sx: {
                    zIndex: 50001,
                  },
                },
              }}>
              <IconButton
                aria-label="delete presets"
                onClick={() => setDeleteMode(v => !v)}
                sx={{
                  borderColor: deleteMode ? 'error.main' : 'divider',
                  mr: 0.5,
                  color: deleteMode ? 'error.main' : 'text.secondary',
                  '&:hover': { color: 'error.main' },
                }}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            {presets.map(p => (
              <PresetCard
                key={p.id}
                preset={p}
                selected={p.id === selectedId}
                deleteMode={deleteMode}
                onSelect={() => handleSelect(p)}
                onDelete={() => handleDelete(p.id)}
              />
            ))}
          </Box>
          <Stack
            mt={2}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button
              startIcon={<CancelPresentationIcon sx={{ scale: '1.2' }} />}
              //   variant={removeMode ? 'contained' : 'outlined'}
              variant="inherit"
              onClick={() => {
                setRemoveMode(o => !o);
                dispatch(setSelectedSinglePreset(null));
              }}
              sx={{
                textTransform: 'none',
                width: { xs: '100%', sm: 'auto' },
                fontSize: '1rem',
                color: removeMode ? '#ff0000ff' : '#00000095',
                border: removeMode ? '2px solid #ff0000ff' : '2px solid #00000075',
              }}>
              REMOVE
            </Button>
          </Stack>
          <Typography
            variant="caption"
            sx={{ color: HEX.subText, mt: 1, display: 'block', fontSize: '1rem' }}>
            {removeModeActive
              ? 'Remove mode is active. Click “remove” again to exit.'
              : deleteMode
                ? 'Delete mode: click a preset to remove it.'
                : 'Click a preset to select it.'}
          </Typography>
        </Box>
      </>
    </Box>
  );
}
